import { useState, useEffect, useCallback, useRef } from 'react'
import { createEmptyGrid, cloneGrid, generateId } from '../utils/gridHelpers.js'

const API_BASE = '/api'

/**
 * Hook for managing store CRUD and grid editing state.
 */
export function useStore(storeId) {
  const [stores, setStores] = useState([])
  const [store, setStore] = useState(null)
  const [zones, setZones] = useState([])
  const [grid, setGrid] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Undo/redo stacks
  const undoStack = useRef([])
  const redoStack = useRef([])
  const saveTimer = useRef(null)

  // Fetch all stores
  const fetchStores = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/stores`)
      if (res.ok) {
        const data = await res.json()
        setStores(data.stores || [])
      }
    } catch (err) {
      console.error('Failed to fetch stores:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch single store + zones
  const fetchStore = useCallback(async (id) => {
    setLoading(true)
    try {
      const [storeRes, zonesRes] = await Promise.all([
        fetch(`${API_BASE}/stores/${id}`),
        fetch(`${API_BASE}/stores/${id}/zones`),
      ])

      if (storeRes.ok) {
        const data = await storeRes.json()
        setStore(data.store)
        const gridData = JSON.parse(data.store.grid_data || '[]')
        const parsedGrid = gridData.length > 0
          ? gridData
          : createEmptyGrid(data.store.grid_width, data.store.grid_height)
        setGrid(parsedGrid)
      }

      if (zonesRes.ok) {
        const data = await zonesRes.json()
        setZones(data.zones || [])
      }
    } catch (err) {
      console.error('Failed to fetch store:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    if (storeId) {
      fetchStore(storeId)
    } else {
      fetchStores()
    }
  }, [storeId, fetchStore, fetchStores])

  // Create a new store
  const createStore = useCallback(async (name, gridWidth = 20, gridHeight = 30) => {
    try {
      const res = await fetch(`${API_BASE}/stores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, grid_width: gridWidth, grid_height: gridHeight }),
      })
      if (res.ok) {
        const data = await res.json()
        await fetchStores()
        return data.store
      }
    } catch (err) {
      console.error('Failed to create store:', err)
    }
    return null
  }, [fetchStores])

  // Delete a store
  const deleteStore = useCallback(async (id) => {
    try {
      await fetch(`${API_BASE}/stores/${id}`, { method: 'DELETE' })
      await fetchStores()
    } catch (err) {
      console.error('Failed to delete store:', err)
    }
  }, [fetchStores])

  // Paint a cell on the grid
  const paintCell = useCallback((x, y, cellType) => {
    setGrid(prev => {
      if (!prev || !prev[y] || prev[y][x] === cellType) return prev

      // Push current state to undo stack
      undoStack.current.push(cloneGrid(prev))
      redoStack.current = [] // Clear redo on new action

      // Keep undo stack bounded
      if (undoStack.current.length > 50) undoStack.current.shift()

      const next = cloneGrid(prev)
      next[y][x] = cellType
      return next
    })

    // Debounced save
    scheduleSave()
  }, [])

  // Paint a range of cells (drag painting)
  const paintCells = useCallback((cells, cellType) => {
    setGrid(prev => {
      if (!prev) return prev

      undoStack.current.push(cloneGrid(prev))
      redoStack.current = []
      if (undoStack.current.length > 50) undoStack.current.shift()

      const next = cloneGrid(prev)
      for (const [x, y] of cells) {
        if (next[y] && next[y][x] !== undefined) {
          next[y][x] = cellType
        }
      }
      return next
    })

    scheduleSave()
  }, [])

  // Undo
  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return
    setGrid(prev => {
      redoStack.current.push(cloneGrid(prev))
      return undoStack.current.pop()
    })
    scheduleSave()
  }, [])

  // Redo
  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return
    setGrid(prev => {
      undoStack.current.push(cloneGrid(prev))
      return redoStack.current.pop()
    })
    scheduleSave()
  }, [])

  // Save grid to server (debounced)
  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => saveGrid(), 1500)
  }, [storeId])

  const saveGrid = useCallback(async () => {
    if (!storeId || !grid) return
    setSaving(true)
    try {
      await fetch(`${API_BASE}/stores/${storeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grid_data: JSON.stringify(grid) }),
      })
    } catch (err) {
      console.error('Failed to save grid:', err)
    } finally {
      setSaving(false)
    }
  }, [storeId, grid])

  // Save zones
  const saveZones = useCallback(async (updatedZones) => {
    if (!storeId) return
    try {
      await fetch(`${API_BASE}/stores/${storeId}/zones`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zones: updatedZones }),
      })
      setZones(updatedZones)
    } catch (err) {
      console.error('Failed to save zones:', err)
    }
  }, [storeId])

  // Force save now (on unmount or explicit save)
  const forceSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveGrid()
  }, [saveGrid])

  return {
    // List view
    stores,
    loading,

    // Single store
    store,
    grid,
    zones,
    saving,

    // Actions
    createStore,
    deleteStore,
    paintCell,
    paintCells,
    undo,
    redo,
    saveZones,
    forceSave,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
  }
}
